import React, { useState } from 'react';
import { Button } from '@/ui-components/ui/button';
import { Input } from '@/ui-components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/util-components/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui-components/ui/tooltip';
import { Kbd } from '@/ui-components/ui/kbd';
import { Undo, Redo, Square, Circle as CircleIcon, Type, Image as ImageIcon, Trash2, ChevronDown, Save, Plus, Download, HelpCircle,
  Component, PenTool
} from 'lucide-react';
import {
  useTemplate,
  useSelectedElementId,
  useCanUndo,
  useCanRedo,
  useTemplateStore
} from '../hooks/useTemplateStore';
import { useTemplateExport } from '../../hooks/useTemplateExport';
import { AspectRatio } from '../../core/template.schemas';
import { ASPECT_RATIO_CONFIG } from '../../core/aspect-ratio.config';
import { elementRegistry } from '../registry';
import Konva from 'konva';
import { CreateTemplateDialog } from '../../components/CreateTemplateDialog';
import { DeleteTemplateDialog } from '../../components/DeleteTemplateDialog';
import { TemplateInfoDialog } from '../../components/TemplateInfoDialog';
import { useTemplatePermissions } from '../hooks/useTemplatePermissions';
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';
import { usePermissions } from '@/lib/hooks/use-permissions';

interface ToolbarProps {
  onSave: () => void;
  onDelete?: () => void;
  onNew?: () => void;
  onCreateTemplate?: (templateId: string) => void;
  stageRef?: React.RefObject<Konva.Stage>;
}

export const Toolbar: React.FC<ToolbarProps> = ({ onSave, onDelete, onNew, onCreateTemplate, stageRef }) => {
  const template = useTemplate();
  const selectedElementId = useSelectedElementId();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const updateTemplateTitle = useTemplateStore(s => s.updateTemplateTitle);
  const updateTemplateAspectRatio = useTemplateStore(s => s.updateTemplateAspectRatio);
  const addElement = useTemplateStore(s => s.addElement);
  const deleteElement = useTemplateStore(s => s.deleteElement);
  const undo = useTemplateStore(s => s.undo);
  const redo = useTemplateStore(s => s.redo);
  
  const { exportCurrentTemplate } = useTemplateExport();
  
  const [, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(template.title);
  const [isExporting, setIsExporting] = useState(false);
  const { canEditTemplate } = useTemplatePermissions();
  const { can } = usePermissions();

  const canEdit = canEditTemplate;
  const canCreate = canEditTemplate;

  const handleAddShape = (type: 'rect' | 'circle' | 'text' | 'image' | 'svg') => {
    const definition = elementRegistry.get(type);
    if (!definition) {
      console.error(`No definition found for element type: ${type}`);
      return;
    }
    addElement(type);
  };

  const handleTitleSave = () => {
    updateTemplateTitle(tempTitle);
    setIsEditingTitle(false);
  };

  const handleExport = async () => {
    if (!stageRef?.current) {
      console.error('Stage reference not available for export');
      return;
    }

    setIsExporting(true);
    try {
      await exportCurrentTemplate(template, stageRef, {
        scale: 2,
        quality: 1,
        filename: `${template.title || 'template'}.png`,
        download: true,
      });
    } catch (error) {
      console.error('Export failed:', error);
      // You could add a toast notification here
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="bg-white border-t border-gray-200 px-3 py-2">
      <div className="flex items-center justify-center gap-1">
        {/* Minimalist Logo - Gray Style */}
        <div className="flex items-center">
          <div className="bg-gray-50 border border-gray-100 p-1.5 rounded-lg shadow-sm">
            <PenTool className="h-4 w-4 text-gray-500" strokeWidth={2.5} />
          </div>
        </div>

        {/* Template Info & Actions */}
        <div className="flex items-center gap-2">
          {/* Template Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-sm font-medium text-gray-700 hover:bg-gray-100 max-w-[160px]">
                <span className="truncate">{template.title}</span>
                <ChevronDown className="ml-1 h-3 w-3 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <div className="p-3 space-y-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">Template Name</label>
                  <Input
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    className="h-7 text-xs"
                    placeholder="Template Name"
                    onBlur={handleTitleSave}
                    disabled={!can(Permission.MANAGE_ENTITIES)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">Seitenverhältnis</label>
                  <Select
                    value={template.aspectRatio}
                    onValueChange={(value: AspectRatio) => updateTemplateAspectRatio(value)}
                    disabled={!can(Permission.MANAGE_ENTITIES)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <div className="flex items-center gap-2">
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {ASPECT_RATIO_CONFIG.map((config) => (
                        <SelectItem key={config.value} value={config.value}>
                          <div className="flex items-center gap-2">
                            {config.icon}
                            <span>{config.value} ({config.label})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DropdownMenuSeparator />
              
              {canCreate && (
                <CreateTemplateDialog
                  trigger={
                    <DropdownMenuItem 
                      className="text-xs"
                      onSelect={(e) => e.preventDefault()}
                    >
                      <Plus className="mr-2 h-3 w-3" />
                      Neues Template
                    </DropdownMenuItem>
                  }
                  onSuccess={(templateId) => {
                    if (onCreateTemplate) {
                      onCreateTemplate(templateId);
                    }
                  }}
                />
              )}
              
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <DropdownMenuItem onClick={onSave} className="text-xs">
                  <Save className="mr-2 h-3 w-3" />
                  Speichern
                </DropdownMenuItem>
              </PermissionFeatureWrapper>
              
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <DropdownMenuItem 
                  onClick={handleExport}
                  disabled={isExporting}
                  className="text-xs"
                >
                  <Download className="mr-2 h-3 w-3" />
                  {isExporting ? 'Exportiere...' : 'Als PNG exportieren'}
                </DropdownMenuItem>
              </PermissionFeatureWrapper>
              
              <DropdownMenuSeparator />
              
              <TemplateInfoDialog
                trigger={
                  <DropdownMenuItem 
                    className="text-xs"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <HelpCircle className="mr-2 h-3 w-3" />
                    Hilfe & Anleitung
                  </DropdownMenuItem>
                }
              />
              
                <DeleteTemplateDialog
                  templateId={template.id}
                  templateTitle={template.title}
                  onSuccess={() => {
                    // Navigate back after deletion
                    window.history.back();
                  }}
                  trigger={
                    <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                      <DropdownMenuItem 
                        className="text-xs text-red-600 focus:text-red-600 focus:bg-red-50"
                        onSelect={(e) => e.preventDefault()}
                      >
                        <Trash2 className="mr-2 h-3 w-3" />
                        Template löschen
                      </DropdownMenuItem>
                    </PermissionFeatureWrapper>
                  }
                />
            </DropdownMenuContent>
          </DropdownMenu>

        {/* Divider */}
        <div className="w-px h-4 bg-gray-300" />

        {/* History Controls */}
        <div className="flex items-center">
          <TooltipProvider>
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={undo} 
                      disabled={!canUndo}
                      className="h-8 w-8 p-0 hover:bg-gray-100 disabled:opacity-40"
                    >
                      <Undo className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="flex items-center gap-2">
                      Rückgängig <Kbd>⌘Z</Kbd>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </PermissionFeatureWrapper>

              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={redo} 
                      disabled={!canRedo}
                      className="h-8 w-8 p-0 hover:bg-gray-100 disabled:opacity-40"
                    >
                      <Redo className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="flex items-center gap-2">
                      Wiederholen <Kbd>⌘Y</Kbd>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </PermissionFeatureWrapper>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onSave}
                      className="h-8 w-8 p-0 hover:bg-gray-100"
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                  </PermissionFeatureWrapper>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="flex items-center gap-2">
                    Speichern <Kbd>⌘S</Kbd>
                  </p>
                </TooltipContent>
              </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
        {canEdit && (
          <>
            <div className="w-px h-4 bg-gray-300" />
            {/* Shape Tools */}
            <div className="flex items-center gap-1 bg-gray-50 rounded-md p-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleAddShape('rect')}
                  className="h-7 w-7 p-0 hover:bg-white hover:shadow-sm"
                >
                  <Square className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleAddShape('circle')}
                  className="h-7 w-7 p-0 hover:bg-white hover:shadow-sm"
                >
                  <CircleIcon className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleAddShape('text')}
                  className="h-7 w-7 p-0 hover:bg-white hover:shadow-sm"
                >
                  <Type className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0 hover:bg-white hover:shadow-sm"
                  onClick={() => handleAddShape('image')}
                >
                  <ImageIcon className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0 hover:bg-white hover:shadow-sm"
                  onClick={() => handleAddShape('svg')}
                >
                  <Component className="h-3.5 w-3.5" />
                </Button>
            </div>
            
            <div className="w-px h-4 bg-gray-300" />
            
            {/* Element Actions */}
            {canEdit && (
            <div className="flex items-center">
              <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => selectedElementId && deleteElement(selectedElementId)}
                        disabled={!selectedElementId}
                        className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                  <TooltipContent>
                    <p className="flex items-center gap-2">
                      Löschen <Kbd>⌫</Kbd>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            )}
          </>
        )}


      </div>
    </div>
  );
};
